import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions, WriteRequestOptions } from '../../requestOptions';
import type {
  ExpansionGapsSheetExportJob,
  ExpansionGapsSheetPlanResponse,
  ExpansionGapsSheetPrepareResponse,
  ExpansionRefreshFromCatalogResponse,
} from './types';

/**
 * The Gaps sheet: ONE cross-category fill-in Super Template for the whole run, imported back
 * through Astur's "Super Template" preset (`catalog_s`). Every route is org-scoped like the other
 * run endpoints. See `gaps-sheet-v2-brief.md`. Generation runs in the background (`export` +
 * `export/{id}`) — a 47K-product run reading every current Astur value takes a while.
 */

type RunParams = {
  runId: number;
  /** Any subscription of the organization — see `getRunFamilies`. */
  subscriptionId: number;
};

const validateRun = (runId: number, subscriptionId: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
};

const headers = (subscriptionId: number) => ({ 'x-astur-subscription-id': subscriptionId.toString() });

/**
 * A dry run across EVERY category in the run: which gap keys already have an Astur field
 * (`ready`), which need a custom field + subscription rule created first
 * (`create_field_and_rule`), and which cannot be imported at all (`not_importable`). Nothing is
 * saved. Keys are deduped across categories — see `ExpansionGapsSheetKey.categories`.
 */
export const getGapsSheetPlan = async (
  scope: RequestScope,
  { runId, subscriptionId }: RunParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionGapsSheetPlanResponse>> => {
  try {
    validateRun(runId, subscriptionId);

    const result = await scopedChannelApi(scope).post<ExpansionGapsSheetPlanResponse>(
      `atts/expansion/runs/${runId}/gaps-sheet/plan`,
      {},
      // A dry run: a POST that saves nothing, so it is a read to the session layer (see `previewResolution`).
      { ...options, headers: headers(subscriptionId), _sessionSemanticRead: true }
    );

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/**
 * Creates the Astur custom field (where missing) and a subscription commonality rule for each
 * checked `create_field_and_rule` key, through the normal resolution create — audited and undoable,
 * and it appears under "Rules created from this run" like any other Resolve rule. Works across
 * every category, the same as the plan.
 */
export const prepareGapsSheet = async (
  scope: RequestScope,
  { runId, subscriptionId, keys, createdBy }: RunParams & { keys: readonly string[]; createdBy?: string },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<ExpansionGapsSheetPrepareResponse>> => {
  try {
    validateRun(runId, subscriptionId);
    if (keys.length === 0) {
      throw new Error('At least one key is required');
    }

    const result = await scopedChannelApi(scope).post<ExpansionGapsSheetPrepareResponse>(
      `atts/expansion/runs/${runId}/gaps-sheet/prepare`,
      { keys, ...(createdBy ? { created_by: createdBy } : {}) },
      { ...options, headers: headers(subscriptionId) }
    );

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/**
 * Starts the background export of the whole-run workbook (202). `gapsOnly` toggles between every
 * product (gaps highlighted) and only the products with ≥1 open gap. Returns the full job — if an
 * unfinished export for this run and option already exists (younger than 2h), that job is handed
 * back as-is and nothing new is queued. Poll with `getGapsSheetExportStatus`.
 */
export const createGapsSheetExport = async (
  scope: RequestScope,
  { runId, subscriptionId, gapsOnly }: RunParams & { gapsOnly: boolean },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<ExpansionGapsSheetExportJob>> => {
  try {
    validateRun(runId, subscriptionId);

    const result = await scopedChannelApi(scope).post<ExpansionGapsSheetExportJob>(
      `atts/expansion/runs/${runId}/gaps-sheet/export`,
      { gaps_only: gapsOnly },
      { ...options, headers: headers(subscriptionId) }
    );

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/** One read of a background export job — `pending`/`in-progress` (optionally with `progress`), `success` (with `url`+`file_name`), or `failed` (with `error`). */
export const getGapsSheetExportStatus = async (
  scope: RequestScope,
  { runId, subscriptionId, exportId }: RunParams & { exportId: string | number },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionGapsSheetExportJob>> => {
  try {
    validateRun(runId, subscriptionId);

    const result = await scopedChannelApi(scope).get<ExpansionGapsSheetExportJob>(`atts/expansion/runs/${runId}/gaps-sheet/export/${exportId}`, {
      ...options,
      headers: headers(subscriptionId),
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/** The job's own name when it has one (always, once created); a plain fallback otherwise. */
export const gapsSheetExportFilename = (fileName: string | null | undefined): string => fileName || 'gaps-sheet.xlsx';

/**
 * Fetches the finished export directly from its temporary URL — a 24h pre-signed S3 link with no
 * Astur auth of its own, so this goes straight through `fetch` rather than the scoped API client,
 * and deliberately sends no Astur headers. Mirrors `fetchSuperTemplateFile`. The name to save under
 * is the job's own `file_name`, not anything parsed off the response.
 */
export const fetchGapsSheetExportFile = async (
  url: string,
  fileName: string | null | undefined,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error('Failed to download the gaps sheet');
  const blob = await response.blob();
  return { blob, filename: gapsSheetExportFilename(fileName) };
};

/**
 * No AI: re-reads gap products from Astur for keys that now have an Astur field (all, or one
 * category) and fills rows whose value is now present, flipping products to ready where that
 * finishes them. `groupingId` omitted refreshes the whole run. Unchanged by v2.
 */
export const refreshFromCatalog = async (
  scope: RequestScope,
  { runId, subscriptionId, groupingId }: RunParams & { groupingId?: number },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<ExpansionRefreshFromCatalogResponse>> => {
  try {
    validateRun(runId, subscriptionId);
    if (groupingId !== undefined && groupingId < 1) {
      throw new Error('Valid grouping ID is required');
    }

    const result = await scopedChannelApi(scope).post<ExpansionRefreshFromCatalogResponse>(
      `atts/expansion/runs/${runId}/refresh`,
      groupingId !== undefined ? { grouping_id: groupingId } : {},
      { ...options, headers: headers(subscriptionId) }
    );

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
